import { useState } from 'react'

import { Box, Spinner, useClassNames } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  ShiftManagementAutoScheduleStatusDocument,
  ShiftManagementCoverageSectionDocument,
  ShiftManagementCoverageShiftsDocument,
  ShiftManagementEnumsAutoSchedulerExecutionStatusEnum,
} from 'generated/resources/graphql'
import { useLocalStorage } from 'usehooks-ts'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  Column,
  ColumnsType,
  gridTemplateColumns,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import useFetchStats from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchStats'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import useLocationIdsFilter from 'modules/shift_management/hooks/useLocationIdsFIlter'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import {
  AutomaticScheduleStatus,
  AutomaticScheduleStatusMessages,
} from 'modules/shift_management/lib/consts/ShiftManagementAutomation'
import {
  COVERAGE_SECTION_HEIGHT,
  COVERAGE_SECTION_MAX_HEIGHT,
  COVERAGE_SECTION_MIN_HEIGHT,
} from 'modules/shift_management/lib/consts/ShiftManagementCoverage'
import type { View } from 'modules/shift_management/lib/types'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import AutomationInProgress from './AutomationInProgress'
import Body from './Body'
import Header from './Header/index'
import * as stylesRefs from './index.vanilla.css'

interface CoverageSectionProps {
  columns: ColumnsType
  view: View
  hover: boolean
  setHover: (hover: boolean) => void
  resizeRef: React.RefObject<HTMLDivElement>
}

const CoverageSection = ({ columns, view, hover, setHover, resizeRef }: CoverageSectionProps) => {
  const [localStorageResizeHeight] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.shiftManagementCoverageResizeHeight,
    0
  )
  const [resizeHeight, setResizeHeight] = useState<number>(
    localStorageResizeHeight ?? COVERAGE_SECTION_HEIGHT
  )
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [automationInProgress, setAutomationInProgress] = useState<boolean>(false)
  const [, dispatchShiftsRows] = useShiftsRows()
  const { refetchData } = useFetchStats({ fetchPolicy: 'cache-first' })
  const $coverageSection =
    typeof document !== 'undefined' ? document.querySelector('#coverage-section') : null
  const isResizing = $coverageSection?.getAttribute('data-resizing') === 'true'

  const cx = useClassNames(stylesRefs)
  const firstCellWidth = columns[0]?.minWidth || 's240'
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const newGraphQLVersion = hasFeatureFlag('dev_shift_management_graphql_migration')
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')

  const [filters] = useFilters()
  const locationIds = useLocationIdsFilter()

  const locationId = Number(locationIds.at(0))
  const employeeIds = filters.employee_ids?.map((employeeId) => Number(employeeId))

  const multipleLocationSelected = locationIds.length > 1

  const rangeParams = {
    startAt: view.startAt,
    endAt: view.endAt,
    locationId,
  }

  const isSolvingInProgress = (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) =>
    status === AutomaticScheduleStatus.SOLVING_ACTIVE ||
    status === AutomaticScheduleStatus.SOLVING_SCHEDULED

  const isSolvingCompleted = (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) => status === AutomaticScheduleStatus.SOLVING_COMPLETED

  const isSolvingFailed = (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) =>
    [
      AutomaticScheduleStatus.SOLVING_FAILED,
      AutomaticScheduleStatus.SOLVING_INCOMPLETE,
      AutomaticScheduleStatus.SOLVING_KILLED,
    ].includes(status)

  const { data, loading } = useQuery(ShiftManagementCoverageSectionDocument, {
    variables: rangeParams,
    skip: !newGraphQLVersion,
  })

  const { refetch } = useQuery(ShiftManagementCoverageShiftsDocument, {
    variables: {
      employeeIds,
      ...rangeParams,
    },
  })

  const {
    data: scheduleData,
    stopPolling,
    startPolling,
  } = useQuery(ShiftManagementAutoScheduleStatusDocument, {
    variables: {
      locationId: Number(locationId),
      first: 1,
      sortOrder: { field: 'updated_at', order: 'desc' },
    },
    skip: !locationId,
    pollInterval: 3000,
  })
  const [lastExecutionStatus, setLastExecutionStatus] = useState<
    ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  >(scheduleData?.shiftManagement?.autoSchedulerExecutionsConnection?.nodes?.at(0)?.status)

  useUnsafeEffect(() => {
    if (!scheduleData) return

    const status =
      scheduleData?.shiftManagement?.autoSchedulerExecutionsConnection?.nodes?.[0]?.status

    const statusChanged = status && status !== lastExecutionStatus
    if (statusChanged || isSolvingInProgress(status)) {
      handleStatusChange(status)
    }
  }, [scheduleData])

  useEffect(() => {
    if (automationInProgress) {
      startPolling(3000)
    } else {
      stopPolling()
    }
  }, [automationInProgress])

  useUnsafeEffect(() => {
    setResizeHeight(localStorageResizeHeight)
  }, [localStorageResizeHeight])

  const handleStatusChange = async (
    status: ShiftManagementEnumsAutoSchedulerExecutionStatusEnum | undefined
  ) => {
    if (isSolvingInProgress(status)) {
      setLastExecutionStatus(status)
      setAutomationInProgress(true)
      return
    }

    if (isSolvingCompleted(status)) {
      if (!lastExecutionStatus) {
        setAutomationInProgress(false)
        return
      }
      await refetchData()
      const { data: shiftData } = await refetch()
      const { shifts: resultShifts = [], restDays: resultRestDays = [] } =
        shiftData?.shiftManagement

      const shiftsToUpdate = resultShifts?.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift?.employee?.fullName || '',
          employeeAvatar: shift?.employee?.avatar?.url || '',
          employeeLocationId: shift?.employee?.locationId || 0,
        })
      )
      const restDaysToUpdate = resultRestDays?.map((restDay) =>
        RestDay.build({
          id: restDay.id,
          employee_id: restDay.employee?.id || 0,
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        })
      )
      setLastExecutionStatus(status)
      setAutomationInProgress(false)

      dispatchShiftsRows({ type: 'RESET_SHIFTS', shifts: shiftsToUpdate, view })
      dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays: restDaysToUpdate })
      app.setFlash({
        type: 'correct',
        message:
          AutomaticScheduleStatusMessages.solving_completed() ??
          i18n.t('shift_management.coverage_section.automation.modal.responses.default_success'),
      })
      return
    }

    if (isSolvingFailed(status) && status) {
      setAutomationInProgress(false)
      if (!lastExecutionStatus) {
        setLastExecutionStatus(status)
        return
      }
      setLastExecutionStatus(status)

      app.setFlash({
        type: 'wrong',
        message:
          AutomaticScheduleStatusMessages[status]?.() ??
          i18n.t('shift_management.coverage_section.automation.modal.responses.default_error'),
      })
    }
  }

  const unassignedShifts: UnassignedShifts = useUnsafeMemo(
    () => UnassignedShifts.buildFromGraphQL(data?.shiftManagement?.unassignedShifts || []),
    [data?.shiftManagement.unassignedShifts]
  )

  useUnsafeEffect(() => {
    if (!newGraphQLVersion) return
    dispatchUnassignedShifts({ type: 'BOOTSTRAP', unassignedShifts })
  }, [newGraphQLVersion, dispatchUnassignedShifts, unassignedShifts])

  if (loading)
    return (
      <Box alignItems="center" width="full" padding="s32" background="grey000White" height="s140">
        <Spinner />
      </Box>
    )

  return (
    <>
      <Header columns={columns} hover={hover} setHover={setHover} resizeRef={resizeRef} />
      <>
        <Box
          id="coverage-section"
          width="full"
          style={{
            height: `${resizeHeight}px`,
            overflow: 'hidden',
            minHeight: `${COVERAGE_SECTION_MIN_HEIGHT}px`,
            maxHeight: `${COVERAGE_SECTION_MAX_HEIGHT}px`,
            transition: (() => {
              return !isResizing ? 'height 0.25s ease-in-out' : undefined
            })(),
          }}
        >
          {automationInProgress && !newEntryFlowEnabled ? (
            <AutomationInProgress />
          ) : multipleLocationSelected && newEntryFlowEnabled ? (
            <Box background="grey100" height="full">
              <OneEmptyState
                emoji="📍"
                title=""
                description={i18n.t(
                  'shift_management.coverage_section.select_one_workplace.description'
                )}
              />
            </Box>
          ) : newEntryFlowEnabled && (view.isDaily || view.isMonthly) ? (
            <Box background="grey100" height="full">
              <OneEmptyState
                emoji="📆"
                title=""
                description={i18n.t(
                  'shift_management.coverage_section.select_weekly_view.description'
                )}
              />
            </Box>
          ) : (
            <Body
              columns={columns}
              view={view}
              setIsSubscribed={setAutomationInProgress}
              automationInProgress={automationInProgress}
              resizeHeight={resizeHeight}
            />
          )}
        </Box>
        <Box
          className={cx('gridHeader')}
          style={{
            ...gridTemplateColumns(firstCellWidth, view, 'content', 0),
          }}
        >
          {columns.map((heading: Column, index: number) =>
            index != 0 ? null : (
              <Box
                key={index}
                background="grey100"
                border={{
                  right: {
                    width: 's1',
                    style: 'solid',
                    color: 'grey300',
                  },
                }}
                className={cx('header', 'sticky')}
              />
            )
          )}
        </Box>
      </>
    </>
  )
}

export default CoverageSection
